import * as React from 'react';
import { TrendingUp } from 'lucide-react';
import { cn } from '../../utils';
import { Card, CardContent, CardHeader, CardTitle } from '../core/card';
import { Avatar, AvatarImage, AvatarFallback } from '../core/avatar';
import { Skeleton } from '../core/skeleton';

export interface TrendingPost {
  id: string;
  title: string;
  excerpt?: string;
  thumbnailUrl?: string | null;
  author: {
    name: string;
    avatar?: string | null;
  };
  likeCount: number;
  commentCount: number;
  spaceId?: string;
  href: string;
}

export interface TrendingPostsProps {
  posts: TrendingPost[];
  isLoading?: boolean;
  emptyLabel?: string;
  title?: string;
  viewAllHref?: string;
  viewAllLabel?: string;
  emptyActionHref?: string;
  emptyActionLabel?: string;
  onPostClick?: (post: TrendingPost) => void;
  className?: string;
}

function TrendingPostsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 shrink-0 rounded-lg" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function TrendingPosts({
  posts,
  isLoading,
  emptyLabel = 'No trending posts',
  title = 'Trending posts',
  viewAllHref,
  viewAllLabel = 'View all',
  emptyActionHref,
  emptyActionLabel = 'View all',
  onPostClick,
  className,
}: TrendingPostsProps) {
  return (
    <Card className={cn('flex flex-col rounded-2xl', className)}>
      <CardHeader className="shrink-0 pb-3">
        <CardTitle as="h3" className="flex items-center gap-2 text-base font-semibold">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
          {title}
        </CardTitle>
      </CardHeader>
      {/* min-h-0 lets the list scroll when the consumer caps the card's height */}
      <CardContent className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <TrendingPostsSkeleton />
        ) : posts.length === 0 ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">{emptyLabel}</p>
            {emptyActionHref && (
              <a
                href={emptyActionHref}
                className="block text-sm font-medium text-brand hover:underline"
              >
                {emptyActionLabel}
              </a>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((post) => (
              <a
                key={post.id}
                href={post.href}
                onClick={(e) => {
                  if (onPostClick) {
                    e.preventDefault();
                    onPostClick(post);
                  }
                }}
                className="flex items-center gap-3 rounded-lg p-1.5 -mx-1.5 transition-colors hover:bg-muted/50"
                aria-label={post.title}
              >
                {post.thumbnailUrl ? (
                  <img
                    src={post.thumbnailUrl}
                    alt=""
                    className="h-10 w-10 shrink-0 rounded-lg object-cover"
                  />
                ) : (
                  <Avatar className="h-10 w-10 shrink-0 rounded-lg">
                    <AvatarImage src={post.author.avatar ?? undefined} alt="" />
                    <AvatarFallback className="rounded-lg text-xs">
                      {getInitials(post.author.name)}
                    </AvatarFallback>
                  </Avatar>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground line-clamp-2">
                    {post.title}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {post.author.name}
                  </p>
                </div>
              </a>
            ))}
          </div>
        )}
        {viewAllHref && posts.length > 0 && (
          <a
            href={viewAllHref}
            className="mt-3 block text-sm font-medium text-brand hover:underline"
          >
            {viewAllLabel}
          </a>
        )}
      </CardContent>
    </Card>
  );
}
